import { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { supplierContacts } from "@/lib/vendorContacts";

const splitList = (s) =>
  String(s || "")
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

// Email the PO (PDF attached) to the supplier; the send lands in the PO's log.
export const POEmailDialog = ({ po, supplier, open, onClose, onSent }) => {
  const [to, setTo] = useState(
    po.supplier_notified_to || supplier?.email || supplier?.rep_email || "",
  );
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(po.rush ? `🔥 HOT RUSH — Purchase order ${po.code} — please expedite` : "");
  const [rush, setRush] = useState(!!po.rush);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const rushSubject = `🔥 HOT RUSH — Purchase order ${po.code} — please expedite`;
  const toggleRush = () => {
    const next = !rush;
    setRush(next);
    setSubject(next ? rushSubject : "");
  };

  // Saved vendor contacts to tap into To / CC (supplier email, rep, named team).
  const contacts = supplierContacts(supplier);

  const inList = (list, email) => splitList(list).some((x) => x.toLowerCase() === email.toLowerCase());
  const toggleCc = (email) => {
    const cur = splitList(cc);
    setCc(
      (inList(cc, email) ? cur.filter((x) => x.toLowerCase() !== email.toLowerCase()) : [...cur, email]).join(", "),
    );
  };

  const send = async (e) => {
    e.preventDefault();
    if (!to.includes("@")) {
      toast.error("Enter the supplier's email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/email`, {
        to: to.trim(),
        cc,
        subject: subject.trim(),
        message,
        rush,
      });
      toast.success(`${po.code} emailed to ${data.email.to}`);
      onSent(data.po);
      onClose();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send the purchase order",
      );
    }
    setBusy(false);
  };

  const sends = po.email_log?.length || 0;

  const ContactChips = ({ target }) =>
    contacts.length ? (
      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`po-email-${target}-contacts`}>
        {contacts.map((c) => {
          const on = target === "to" ? to.trim().toLowerCase() === c.email.toLowerCase() : inList(cc, c.email);
          return (
            <button
              key={`${target}-${c.email}`}
              type="button"
              data-testid={`po-email-${target}-contact`}
              onClick={() => (target === "to" ? setTo(c.email) : toggleCc(c.email))}
              title={c.email}
              className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors duration-150 ${
                on
                  ? "border-amber-400/50 bg-amber-400/10 text-amber-200"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
              }`}
            >
              {on ? "✓ " : "+ "}
              {c.label}
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="po-email-dialog"
        className="max-w-lg border-zinc-800 bg-zinc-900"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" className="text-indigo-400" />
            Email {po.code} to the supplier
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            The purchase order goes as a PDF with the items listed in the email.
            {po.status === "Draft" ? " Sending marks the order as Ordered." : ""}
            {sends ? ` Sent ${sends} time${sends === 1 ? "" : "s"} before.` : ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">To</Label>
            <Input
              data-testid="po-email-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="orders@supplier.com"
              className="h-12 border-zinc-800 bg-zinc-950 text-base"
            />
            <ContactChips target="to" />
            {!contacts.length && (
              <p className="text-xs text-zinc-500">
                No email saved on this supplier — add one on the supplier record to
                pre-fill next time.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Cc (optional)
            </Label>
            <Input
              data-testid="po-email-cc"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="rep@supplier.com, you@yourshop.com"
              className="h-12 border-zinc-800 bg-zinc-950 text-base"
            />
            <ContactChips target="cc" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Subject (optional)
              </Label>
              <button
                type="button"
                data-testid="po-email-hot-rush"
                onClick={toggleRush}
                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  rush
                    ? "border-red-500 bg-red-500 text-white"
                    : "border-red-500/50 text-red-300 hover:bg-red-500/10"
                }`}
              >
                🔥 Hot Rush
              </button>
            </div>
            <Input
              data-testid="po-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Purchase order ${po.code} from your shop`}
              className="h-12 border-zinc-800 bg-zinc-950 text-base"
            />
            <p className="text-xs text-zinc-500">
              Leave blank to use the standard subject. Tap Hot Rush to flag it as urgent.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Message (optional)
            </Label>
            <Textarea
              data-testid="po-email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Please ship on the Tuesday truck. Call before delivery."
              className="border-zinc-800 bg-zinc-950 text-base"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              data-testid="po-email-send"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Sending…" : sends ? "Send again" : "Send purchase order"}
            </Button>
            <Button
              type="button"
              data-testid="po-email-cancel"
              variant="outline"
              onClick={onClose}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
